import { Eyebrow } from "@/components/Eyebrow";
import { Reveal } from "@/components/Reveal";
import { Mark } from "@/components/Mark";

const STEPS = [
  {
    n: "01",
    title: "Escuchar al consumidor",
    text: "La encuesta permitió identificar intención de prueba, disposición a pagar, formatos preferidos y los atributos que más valoraba el consumidor.",
    img: "/images/coesgran-encuesta.png",
    alt: "Resultados clave de la encuesta al consumidor de COESGRAN",
    tag: null,
  },
  {
    n: "02",
    title: "Encontrar el hueco",
    text: "Al cruzar consumidor, competencia y precios apareció un espacio estratégico entre las marcas de distribuidor y las propuestas de mayor precio.",
    img: "/images/coesgran-mapa.png",
    alt: "Mapa de posicionamiento: gap estratégico calidad alta, precio medio",
    tag: "Calidad alta · Precio medio",
  },
  {
    n: "03",
    title: "Convertir el hallazgo en propuesta",
    text: "La oportunidad se convirtió en una propuesta basada en origen andaluz, trazabilidad y conveniencia.",
    img: "/images/coesgran-pasaporte.png",
    alt: "Pasaporte Digital de Cosecha de COESGRAN",
    tag: "Origen · Trazabilidad · Confianza",
  },
  {
    n: "04",
    title: "Hacerla tangible",
    text: "La estrategia terminó bajando a producto: formatos, packaging, precio y una identidad capaz de hacer visible el origen.",
    img: "/images/coesgran-packaging.png",
    alt: "Packaging de COESGRAN: tarro 400g y bolsa 800g",
    tag: null,
  },
];

function StepRow({ step, index }) {
  const flip = index % 2 === 1;
  return (
    <Reveal>
      <div
        data-testid={`coesgran-step-${step.n}`}
        className="grid items-center gap-8 md:grid-cols-12 md:gap-12"
      >
        <div className={`md:col-span-4 ${flip ? "md:order-2" : ""}`}>
          <p className="font-serif text-lg italic text-mirian-wine/55">
            {step.n}
          </p>
          <h3 className="mt-3 font-serif text-2xl uppercase leading-tight tracking-wide sm:text-3xl">
            {step.title}
          </h3>
          <p className="mt-5 text-sm leading-relaxed text-mirian-wine/75 md:text-base">
            {step.text}
          </p>
          {step.tag && (
            <p className="mt-5 border-l-2 border-mirian-wine pl-4 text-xs font-medium uppercase tracking-[0.2em] text-mirian-wine/70">
              {step.tag}
            </p>
          )}
        </div>
        <div className={`md:col-span-8 ${flip ? "md:order-1" : ""}`}>
          <img
            data-testid={`coesgran-img-${step.n}`}
            src={step.img}
            alt={step.alt}
            loading="lazy"
            className="h-auto w-full"
          />
        </div>
      </div>
    </Reveal>
  );
}

export default function Coesgran() {
  return (
    <section
      data-testid="coesgran-section"
      className="border-t border-mirian-wine/15 px-6 py-28 md:px-12 md:py-40 lg:px-20"
    >
      <div className="mx-auto max-w-6xl">
        <Eyebrow index="05" label="Proyecto Coesgran × ESIC" />
        <Reveal delay={0.1}>
          <h2 className="mt-10 font-serif text-4xl leading-[1.1] sm:text-5xl lg:text-6xl">
            Coesgran
          </h2>
          <p className="mt-4 max-w-2xl font-serif text-2xl italic leading-snug text-mirian-wine/80 sm:text-3xl">
            Una colaboración con visión de futuro.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-10 max-w-xl space-y-5 text-base leading-relaxed text-mirian-wine/75">
            <p>
              El proyecto nació de una colaboración entre COESGRAN y ESIC con
              un reto real: explorar nuevas oportunidades de crecimiento para
              la compañía en el mercado de vegetales congelados.
            </p>
            <p>
              Partimos del mercado, la competencia y el consumidor para
              detectar dónde podía existir una oportunidad y convertirla en
              decisiones concretas de posicionamiento, producto, precio y
              marca.
            </p>
            <p>
              El resultado fue una propuesta que llevaba a COESGRAN desde el
              análisis hasta una posible nueva línea de negocio.
            </p>
          </div>
        </Reveal>

        <div className="mt-20 space-y-24 md:mt-28 md:space-y-32">
          {STEPS.map((step, i) => (
            <StepRow key={step.n} step={step} index={i} />
          ))}
        </div>

        <Reveal delay={0.15}>
          <p className="mt-24 max-w-3xl font-serif text-2xl leading-snug sm:text-3xl lg:text-4xl md:mt-32">
            Lo que más me interesa de la estrategia ocurre precisamente aquí:
            cuando un dato deja de ser solamente un dato y empieza a{" "}
            <Mark delay={0.5}>cambiar una decisión</Mark>.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
